import type { IconButtonProps } from './IconButton';
import type { ButtonAppearanceProps, IconButtonSize } from 'components/Button/types';

/**
 * Maps the legacy MUI `IconButton` props onto the Design System 2.0 API.
 *
 * TODO(HNS-264): temporary, exactly like `Buttons/Button/toDsProps`. It exists so the visual refresh
 * is gated inside the wrapper rather than at ~76 call sites; retiring the flag deletes it.
 *
 * Returning `null` means "DS 2.0 cannot express this button" and sends the caller back to MUI. A
 * silent best-effort mapping would ship buttons that are the wrong colour or the wrong size, and
 * nothing catches that at review time. Every `null` here is a design question.
 *
 * The accessible name is carried out in the result rather than re-read by the caller: this mapper is
 * what proves the label is present, so it should also be what hands it over already narrowed to
 * `string`. That keeps a non-null assertion out of the wrapper.
 */
type DsProps = ButtonAppearanceProps & { size: IconButtonSize; 'aria-label': string; loading?: boolean };

/**
 * MUI icon-button sizes are 12 / 20 / 32 / 40 / 48; the DS scale reachable from HERE is
 * 16 / 24 / 32 / 40 / 44 - `IconButtonSize`, not `ButtonSize`, because the 16px `3xs` cell (Figma's
 * `Tiny`) is icon-only and so has no text-button counterpart. Only exact matches are mapped:
 * snapping a size up would grow the button and silently reflow whatever sits beside it.
 *
 * `small` maps to `2xs`. The legacy `small` renders at 20px (`StyledIconButton`'s own block, which
 * applies at every variant) against `2xs`'s 24, so this is the one cell that is a deliberate 4px
 * growth rather than an exact match - 24 is the smallest circle the design draws, and the
 * alternative is leaving the most common size in the codebase permanently on MUI.
 *
 * Unmapped, and why:
 *   - `xs` (12) - the DS now draws a 16px circle (`3xs`), but 12 -> 16 is a 33% growth on the
 *     smallest control in the product, so it stays on MUI rather than being snapped up. Mapping it
 *     would also put a below-minimum touch target behind a size name nobody chose for that reason.
 *   - `action` (48) - DS stops at 44.
 */
const SIZE: Partial<Record<NonNullable<IconButtonProps['size']>, IconButtonSize>> = {
  small: '2xs',
  medium: 'xs',
  large: 'regular',
};

/**
 * Cases that stay on MUI:
 *
 *   - `sx` - an Emotion prop with no effect on a Tailwind component. Accepting it would drop the
 *     styles silently rather than fail.
 *   - no `aria-label` - the DS component requires an accessible name, and an icon button without
 *     one is unusable with a screen reader. `IconButtonWithTooltip` supplies it from `title`, so
 *     this only bites on bare `IconButton` call sites (which are already an a11y bug on MUI).
 *   - `variant` omitted - with no variant, `IconButton.styled` applies no block at all and MUI's own
 *     `color` prop is never forwarded, so the button renders as a bare transparent MUI default.
 *     There is no DS cell for "no styling".
 *   - coloured `variant="text"` - `muted / default` draws its icon in `Color/Dark/1` (near-black), so
 *     mapping a deliberately blue or red text button onto it would silently discard the colour.
 *     Note `Buttons/Button/toDsProps` maps ALL text buttons to `muted / default` regardless of colour;
 *     that is worth revisiting there for the same reason.
 *   - `color="info"` - DS 2.0 has no informational appearance.
 *   - `variant="contained" color="secondary"` - a solid neutral circle. The design library never
 *     modelled that cell: its only solid neutral is `secondary / default`, which is a different
 *     colour from the grey[100]-on-grey[700] the legacy control draws. So the legacy control keeps
 *     its own styling until the design answers what a neutral contained icon button should be.
 */
export const toDsIconProps = ({
  color = 'primary',
  isLoading,
  size = 'medium',
  sx,
  variant,
  'aria-label': ariaLabel,
}: IconButtonProps): DsProps | null => {
  if (sx || !ariaLabel) {
    return null;
  }

  const dsSize = SIZE[size];

  if (!dsSize) {
    return null;
  }

  const base = { size: dsSize, 'aria-label': ariaLabel, loading: isLoading };

  // Early returns rather than one built object: `ButtonAppearanceProps` is a discriminated union, so
  // a flat `{ appearance, fill }` assembled from variables satisfies neither member.
  if (variant === 'contained') {
    if (color === 'primary') return { appearance: 'primary', fill: 'default', ...base };
    if (color === 'error') return { appearance: 'destructive', fill: 'default', ...base };

    return null;
  }

  if (variant === 'outlined') {
    if (color === 'primary') return { appearance: 'primary', fill: 'outline', ...base };
    if (color === 'error') return { appearance: 'destructive', fill: 'outline', ...base };
    /**
     * `light` is what `ButtonMenu` defaults to for every non-text variant. Through the colour
     * mappers it resolves to a white background, a `grey[300]` border and a `grey[700]` icon - the
     * same neutral outline `muted / outline` draws, and the same recipe `toDsProps` already maps
     * `variant="outlined-grey"` onto for text buttons.
     *
     * `secondary` and `text` are deliberately absent: both fall through to `secondary.contrastText`
     * for the BORDER, i.e. a dark grey[700] outline rather than a light one. DS has no dark-outline
     * neutral cell, so they stay on MUI.
     */
    if (color === 'light') return { appearance: 'muted', fill: 'outline', ...base };

    return null;
  }

  if (variant === 'text' && color === 'text') {
    return { appearance: 'muted', fill: 'default', ...base };
  }

  /**
   * `color` is deliberately not consulted, because the legacy variant does not consult it either -
   * `StyledIconButton`'s `plain` block hardcodes `text.secondary` and never reads `iconColor`. So
   * there is no colour for `muted / default` to discard, which is exactly what blocks the same
   * mapping for a coloured `text` button above.
   *
   * The one difference the swap introduces is a hover BACKGROUND: `plain` has none, the DS cell draws
   * one. Accepted as a design call rather than a mismatch - a hover affordance on an icon-only
   * control is what DS 2.0 draws everywhere, and the alternative is a bare MUI control on a
   * refreshed page. It is why `plain` was previously refused here.
   */
  if (variant === 'plain') {
    return { appearance: 'muted', fill: 'default', ...base };
  }

  return null;
};
